import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CButton, CCard, CCardBody, CFormSelect, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle, cilGamepad, cilLowVision } from '@coreui/icons'
import moment from 'moment'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import UserService from 'src/service/UserService'
import GameListingModal from '../GameAccount/GameListingModal'
import './User.css'

const SEARCH_FIELDS = [
  { key: 'club_name', label: 'Hub Name' },
  { key: 'full_name', label: 'Hub Admin' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'id', label: 'Hub ID' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '1', label: 'Activated' },
  { value: '0', label: 'Deactivated' },
]

const columns = [
  { label: 'Hub ID', key: 'id', filter: false, sorter: true, _style: { width: '8%' } },
  { label: 'Hub Name', key: 'club_name', filter: false, sorter: true },
  { label: 'Hub Admin', key: 'full_name', filter: false, sorter: true },
  { label: 'Email', key: 'email', filter: false, sorter: true },
  { label: 'Phone', key: 'phone', filter: false, sorter: false },
  { label: 'Registered', key: 'created_at', filter: false, sorter: true },
  { label: 'Status', key: 'is_active', filter: false, sorter: true },
  { label: 'Actions', key: 'action', filter: false, sorter: false, _style: { width: '1%' } },
]

const GameAdmin = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('club_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()
  const [statusUpdatingId, setStatusUpdatingId] = useState(null)

  const [modalIsOpen, setIsOpen] = useState(false)
  const [userDataListing, setUserDataListing] = useState([])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const handleStatusFilter = (value) => {
    setActivePage(1)
    setColumnFilter((prev) => {
      const next = { ...prev }
      if (value === '') delete next.is_active
      else next.is_active = value
      return next
    })
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key] !== '' && columnFilter[key] !== undefined) {
        params.append(key, columnFilter[key])
      }
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.getAdminUserListing(offset, itemsPerPage, activePage, params, true, false)
      .then((result) => {
        setUsers(result.data)
      })
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load game admins', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openGames = (accountId) => {
    setUserDataListing([])
    UserService.getGameUser(accountId)
      .then((result) => {
        setUserDataListing(result.data)
      })
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load games', 'error')
      })
    setIsOpen(true)
  }

  const changeAccountStatus = (userId, status) => {
    setStatusUpdatingId(userId)
    UserService.changeAccountStatus({ userId, status })
      .then((result) => {
        ToastComponent(result.message, 'success')
        fetchData()
      })
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not update hub status', 'error')
      })
      .finally(() => setStatusUpdatingId(null))
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Game Admins</h1>
        <p className="ge-page-subtitle">
          Every hub and its admin. Click a hub name to log in as that game admin.
        </p>
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">
                Manage Admins
                {users?.total !== undefined && <span className="ge-tab-count">{users.total}</span>}
              </h2>
              <p className="um-panel-note">Open a hub&apos;s games, or hide a hub from the app.</p>
            </div>
          </div>

          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
            trailing={
              <CFormSelect
                size="sm"
                aria-label="Filter by status"
                value={columnFilter?.is_active ?? ''}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </CFormSelect>
            }
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              noItemsLabel="No game admins found"
              scopedColumns={{
                id: (item) => <td className="um-muted">{item.id}</td>,
                club_name: (item) => (
                  <td>
                    <a
                      href={`/admin-login?user_id=${item.id}`}
                      className="fw-semibold text-primary text-decoration-none"
                      title={`Login as ${item.club_name || item.full_name} game admin`}
                    >
                      {item.club_name || '-'}
                    </a>
                  </td>
                ),
                full_name: (item) => <td>{item.full_name || '-'}</td>,
                email: (item) => <td>{item.email || '-'}</td>,
                phone: (item) => <td>{item.phone || '-'}</td>,
                created_at: (item) => (
                  <td>{item.created_at ? moment(item.created_at).format('D.MM.YYYY') : '-'}</td>
                ),
                is_active: (item) => (
                  <td>
                    <CBadge
                      color={item.is_active === 1 ? 'success' : 'warning'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.is_active === 1 ? 'Activated' : 'Deactivated'}
                    </CBadge>
                  </td>
                ),
                action: (item) => {
                  if (item.user_role_id !== 3) return <td />
                  const isActive = item.is_active === 1
                  const statusTitle = isActive ? 'Hide Hub' : 'Show Hub'
                  return (
                    <td className="py-2">
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <CButton
                          color="primary"
                          variant="outline"
                          size="sm"
                          className="ge-row-icon-btn"
                          title="Games"
                          aria-label="Games"
                          onClick={() => openGames(item.id)}
                        >
                          <CIcon icon={cilGamepad} />
                        </CButton>
                        <CButton
                          color={isActive ? 'warning' : 'success'}
                          variant="outline"
                          size="sm"
                          className="ge-row-icon-btn"
                          title={statusTitle}
                          aria-label={statusTitle}
                          disabled={statusUpdatingId === item.id}
                          onClick={() => changeAccountStatus(item.id, isActive ? 0 : 1)}
                        >
                          <CIcon icon={isActive ? cilLowVision : cilCheckCircle} />
                        </CButton>
                      </div>
                    </td>
                  )
                },
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      <GameListingModal
        IsModalOpened={modalIsOpen}
        onCloseModal={setIsOpen}
        userDataListing={userDataListing}
      />
    </>
  )
}

export default GameAdmin
